// Machine-readable failure surfaces: one JSON error envelope for every public
// API route, one markdown body for 404s, and the MCP manifest document.
// Browser-safe: pure data + string building, no server imports.

import { MCP_TOOLS, MCP_URL, API_VERSION, ENDPOINTS } from "./agent-surface";
import { SITE } from "./site-content";

export type AgentErrorBody = {
  error: {
    code: string;
    message: string;
    hint: string;
    docs: string;
    status: number;
  };
};

export const DOCS_URL = `${SITE.url}/docs`;

/**
 * CLI tools, HTTP libraries and known AI crawlers. Browsers always send a UA
 * containing "Mozilla", so anything matching this list (or sending no UA at
 * all) is treated as a machine client for content negotiation.
 */
const AGENT_UA =
  /curl|wget|httpie|python|aiohttp|axios|node-fetch|undici|got\/|okhttp|java\/|ruby|perl|gptbot|chatgpt|oai-searchbot|claudebot|claude-user|claude-searchbot|anthropic|perplexity|google-extended|applebot-extended|bytespider|cohere|meta-externalagent|duckassistbot|modelcontextprotocol|\bmcp\b/i;

export function isAgentUserAgent(ua: string | null | undefined): boolean {
  if (!ua || !ua.trim()) return true;
  if (ua.includes("Mozilla")) return false;
  return AGENT_UA.test(ua);
}

export type ApiRouteMatch = { path: string; method: "GET" | "POST" };

/**
 * Matches a request path against the documented public-endpoint registry,
 * expanding `{param}` segments. Used to tell "unknown route" (404) apart
 * from "known route, wrong method" (405).
 */
export function matchApiEndpoint(pathname: string): ApiRouteMatch | null {
  const clean = pathname.replace(/\/+$/, "") || "/";
  for (const e of ENDPOINTS) {
    const pattern = e.path.replace(/[.*+?^${}()|[\]\\]/g, "\\$&").replace(/\\\{(\w+)\\\}/g, "[^/]+");
    if (new RegExp(`^${pattern}$`).test(clean)) return { path: e.path, method: e.method };
  }
  return null;
}

/** The single error shape every /api/public/* endpoint returns on failure. */
export function buildAgentError(
  code: string,
  message: string,
  hint: string,
  status: number,
): AgentErrorBody {
  return { error: { code, message, hint, docs: DOCS_URL, status } };
}

/** Short markdown body served with a 404 so agents can recover on their own. */
export function renderNotFoundMarkdown(path: string): string {
  return [
    `# 404 — not found on ${SITE.name}`,
    "",
    `No page or endpoint exists at \`${path}\`.`,
    "",
    "## Where to look next",
    "",
    `- Sitemap: ${SITE.url}/sitemap.xml`,
    `- Agent index: ${SITE.url}/llms.txt (full: ${SITE.url}/llms-full.txt)`,
    `- Developer docs: ${DOCS_URL}`,
    `- OpenAPI 3.1: ${SITE.url}/api/public/openapi.json`,
    `- MCP server (streamable HTTP, no auth): ${MCP_URL}`,
    `- Search the library: ${SITE.url}/api/public/library/search?q=`,
    "",
    "Any public page is also readable as markdown by prefixing its path with `/md`.",
    "",
    `<!-- api-version: ${API_VERSION} -->`,
  ].join("\n");
}

/** Standard MCP manifest, served at /.well-known/mcp.json and /api/public/mcp.json. */
export function renderMcpManifest(): Record<string, unknown> {
  return {
    schema_version: "2025-06-18",
    name: "vibing-public",
    title: `${SITE.name} — public tools`,
    description:
      "Read-only MCP server: search The Vibeary (2,000+ curated vibe-coding resources), read any page of vibing.inc as markdown, and get services and contact details.",
    version: API_VERSION,
    vendor: SITE.name,
    homepage: SITE.url,
    documentation: DOCS_URL,
    contact: SITE.email,
    transport: "streamable-http",
    url: MCP_URL,
    endpoint: MCP_URL,
    protocolVersion: "2025-06-18",
    authentication: { type: "none" },
    capabilities: { tools: { listChanged: false } },
    servers: [
      {
        name: "vibing-public",
        transport: "streamable-http",
        url: MCP_URL,
        authentication: "none",
      },
    ],
    tools: MCP_TOOLS.map((t) => ({ name: t.name, description: t.description })),
    // In-browser tool surface: any agent running inside the page gets the same
    // library tools plus navigation via document.modelContext (WebMCP).
    webmcp: {
      supported: true,
      documentation: `${SITE.url}/webmcp`,
      api: "document.modelContext",
      polyfill: "@mcp-b/global",
      scope: "every page of vibing.inc",
    },
  };
}

/** Homepage identity graph: Organization + WebSite + the existing Service node. */
export function renderHomeJsonLd(): Record<string, unknown> {
  const org = {
    "@type": "Organization",
    "@id": `${SITE.url}/#organization`,
    name: SITE.name,
    alternateName: ["the Vibing Company®", "Vibing Company", "vibing.inc"],
    legalName: SITE.legalName,
    description:
      "A Lovable-specialised consultancy: we design, build, secure, rescue and ship Lovable and vibe-coded apps for solo founders, teams and companies, and publish The Vibing Library of 2,000+ curated vibe-coding resources.",
    url: `${SITE.url}/`,
    logo: `${SITE.url}/favicon.ico`,
    email: SITE.email,
    founder: { "@type": "Person", name: "Jonathan Osburg" },
    knowsAbout: ["Lovable", "vibe coding", "AI app development", "MCP servers", "Supabase"],
    sameAs: ["https://x.com/thevibingco"],
    address: {
      "@type": "PostalAddress",
      streetAddress: "St.-Klara-Str. 7A",
      postalCode: "82377",
      addressLocality: "Penzberg",
      addressRegion: "Bavaria",
      addressCountry: "DE",
    },
    contactPoint: [
      {
        "@type": "ContactPoint",
        contactType: "customer support",
        email: SITE.email,
        url: `${SITE.url}/contact`,
        availableLanguage: ["en", "de"],
        areaServed: "Worldwide",
      },
      {
        "@type": "ContactPoint",
        contactType: "technical support",
        email: SITE.email,
        url: DOCS_URL,
        availableLanguage: ["en"],
        areaServed: "Worldwide",
      },
    ],
  };

  const website = {
    "@type": "WebSite",
    "@id": `${SITE.url}/#website`,
    name: SITE.name,
    description: SITE.tagline,
    url: `${SITE.url}/`,
    publisher: { "@id": `${SITE.url}/#organization` },
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `${SITE.url}/api/public/library/search?q={search_term_string}`,
        contentType: "application/json",
      },
      "query-input": "required name=search_term_string",
    },
  };

  const service = {
    "@type": "Service",
    "@id": `${SITE.url}/#service`,
    name: "Lovable insider practice for founders and companies",
    description:
      "Lovable advisory, executive briefings, team workshops, vibe coding governance, build and rescue engagements.",
    provider: { "@id": `${SITE.url}/#organization` },
    areaServed: "Worldwide",
    serviceType:
      "Lovable advisory, executive briefings, team workshops, vibe coding governance, build & rescue",
    url: `${SITE.url}/`,
  };

  const api = {
    "@type": "WebAPI",
    "@id": `${SITE.url}/#api`,
    name: `${SITE.name} public API`,
    description:
      "Unauthenticated, CORS-open JSON API and MCP server over The Vibeary, services and guides.",
    documentation: DOCS_URL,
    provider: { "@id": `${SITE.url}/#organization` },
    url: `${SITE.url}/api/public/openapi.json`,
  };

  return { "@context": "https://schema.org", "@graph": [org, website, service, api] };
}
